import React from 'react';
import { Box, Typography, Theme, TypographyVariant } from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { OutputData } from '@editorjs/editorjs';
import ReactPlayer from 'react-player';
interface Props {
  content: OutputData;
}

interface ImageProps {
  data: any;
}

interface ImageStyleProps {
  stretched: boolean;
  bordered: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  imageCaption: {
    margin: `${theme.spacing(1)}px 0px`,
  },
  paragraph: {
    margin: '1em 0px',
  },
}));

const useImageStyles = makeStyles<Theme, ImageStyleProps>((theme: Theme) => ({
  image: {
    border: ({ bordered }) =>
      bordered ? `${theme.palette.grey['500']} solid 1px` : undefined,
    maxWidth: '100%',
    width: ({ stretched }) => (stretched ? '100%' : undefined),
  },
}));

const BlockViewer: React.FC<Props> = ({ content }) => {
  const { imageCaption, paragraph } = useStyles();
  if (!content) return <></>;
  return (
    <>
      {content.blocks.map((block, index) => {
        switch (block.type) {
          case 'paragraph':
            return (
              <Typography
                key={index}
                className={paragraph}
                dangerouslySetInnerHTML={{ __html: block.data.text }}
              />
            );
          case 'header': {
            return (
              <Typography
                key={index}
                variant={`h${block.data.level}` as TypographyVariant}
              >
                {block.data.text}
              </Typography>
            );
          }
          case 'image': {
            return (
              <Box
                key={index}
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                py={1}
                bgcolor={block.data.withBackground ? 'text.secondary' : null}
              >
                <Image data={block.data} />
                <Typography className={imageCaption} variant="caption">
                  {block.data.caption}
                </Typography>
              </Box>
            );
          }
          case 'list': {
            return (
              <Box key={`item${index}`} pl={2}>
                {block.data.items.map((item: any, itemIndex: any) => {
                  return (
                    <Box key={itemIndex} display="flex">
                      <Box mr={1}>
                        <Typography align="center">
                          {block.data.style === 'ordered'
                            ? `${itemIndex + 1}.`
                            : '•'}
                        </Typography>
                      </Box>
                      <Typography dangerouslySetInnerHTML={{ __html: item }} />
                    </Box>
                  );
                })}
              </Box>
            );
          }
          case 'delimiter': {
            return <Box py={1}> </Box>;
          }
          case 'embed': {
            return (
              <Box
                key={index}
                display="flex"
                justifyContent="center"
                alignItems="center"
                flexDirection="column"
                py={1}
              >
                <ReactPlayer
                  url={block.data.embed}
                  controls={true}
                  playing={false}
                  width="100%"
                  height={340}

                />
                <Typography className={imageCaption} variant="caption">
                  {block.data.caption}
                </Typography>
              </Box>
            );
          }
          default:
            return null;
        }
      })}
    </>
  );
};

const Image: React.FC<ImageProps> = ({ data }) => {
  const { image } = useImageStyles({
    stretched: data.stretched,
    bordered: data.withBorder,
  });
  return <img className={image} src={data.file.url} alt="" />;
};

export default BlockViewer;
